import React from 'react';

import { noop } from '@philips-emr/tws-utils';

export interface Data {
  [key: string]: unknown;
}

class DataContext {

  static Context = React.createContext<Data>({});

  private parent: DataContext|null;
  private data: Data = {};

  private listeners: Function[] = [];
  private unlisten: Function = noop;

  constructor(parent: DataContext | null) {
    this.parent = parent;

    if (parent) {
      this.unlisten = parent.addListener(this.fireListeners);
    }
  }

  addListener(listener: Function): Function {
    this.listeners.push(listener);

    return () => {
      const position = this.listeners.indexOf(listener);
      this.listeners.splice(position, 1);
    };
  }

  fireListeners = (): void => {
    const bulkData = this.getBulkData();

    this.listeners.forEach((listener) => listener(bulkData));
  };

  get(key: string): any {
    let value = this.data[key];
    if (value === undefined) {
      if (this.parent) {
        value = this.parent.get(key);
      }
    }
    return value;
  }

  set(data: Data): void {
    Object.assign(this.data, data);
    this.fireListeners();
  }

  keys(): string[] {
    const ks = Object.keys(this.data);
    const parent = this.parent;
    return parent ? parent.keys().concat(ks) :ks;
  }

  getBulkData(): Data {
    const data = this.data || {};

    if (this.parent) {
      const parentData = this.parent ? this.parent.getBulkData() : {};

      return { ...parentData, ...data };
    }

    return { ...data };
  }

  readOnly(): object {
    const ContextProxyHandler: ProxyHandler<Data> = {
      get: (_, sKey: string) => this.get(sKey),
      set: () => false,
      deleteProperty: () => false,
      enumerate: () => this.keys(),
      ownKeys: () => this.keys(),
      has: (oTarget, sKey: string) => sKey in oTarget || !!this.get(sKey),
      defineProperty: () => false,
      getOwnPropertyDescriptor: (_, sKey: string) => {
        const vValue = this.get(sKey);
        return vValue ? { value: vValue, writable: true, enumerable: true, configurable: true } : undefined;
      },
    };
    return new Proxy(this.data, ContextProxyHandler);
  }

  destroy(): void {
    this.unlisten();
  }

}

export default DataContext;
